import { PlainDate } from "@sa-nu/shared-utils/date";

import type {
  CICOSelection,
  ReservationCalendarNavigationSize,
} from "../../reservation-calendar";

export type DateTapAction =
  | "set"
  | "expandCheckIn"
  | "expandCheckOut"
  | "shrinkCheckIn"
  | "shrinkCheckOut"
  | "clear"
  | "ignore";

/**
 * CI/CO日エリアの操作を決定する
 *
 * 基本方針
 * - すでにCI/CO日がある場合
 *   - ボタン表示上隣り合う日付を押すとCI/CO日を増やせる
 *   - ボタン表示上CI/CO日の端を押すとCI/CO日を減らせる
 * - CI/CO日に隣接しないボタンを押すと、新しくCI/CO絞り込みがセットされる
 *
 */
export function resolveDateTapAction(
  date: PlainDate,
  cico: CICOSelection | null,
  navigationSize: ReservationCalendarNavigationSize,
): DateTapAction {
  if (!cico) return "set";

  const { checkIn, checkOut } = cico;
  const nights = checkIn.until(checkOut).total({ unit: "day" });
  const firstNight = checkIn;
  const lastNight = checkOut.subtract({ days: 1 });

  // CI前日タップ → expand or ignore (上限チェック)
  if (date.equals(firstNight.subtract({ days: 1 }))) {
    const newNights = date.until(checkOut).total({ unit: "day" });
    return newNights <= navigationSize ? "expandCheckIn" : "ignore";
  }

  // lastNight+1日(CO日)タップ → expand or ignore (上限チェック)
  if (date.equals(lastNight.add({ days: 1 }))) {
    const newCheckOut = date.add({ days: 1 });
    const newNights = firstNight.until(newCheckOut).total({ unit: "day" });
    return newNights <= navigationSize ? "expandCheckOut" : "ignore";
  }

  // CI日タップ → clear or shrink
  if (date.equals(firstNight)) {
    return nights <= 1 ? "clear" : "shrinkCheckIn";
  }

  // lastNight(CO-1日)タップ → clear or shrink
  if (date.equals(lastNight)) {
    return nights <= 1 ? "clear" : "shrinkCheckOut";
  }

  // 外に接さない内側の日付 → ignore
  if (
    PlainDate.compare(date, firstNight) > 0 &&
    PlainDate.compare(date, lastNight) < 0
  ) {
    return "ignore";
  }

  // 離れた日付 → 新規セット
  return "set";
}
